import { Link, useLocation } from "react-router-dom";
import { Phone } from "lucide-react";
import HeadTags from "../../seo/HeadTags";
import { leesBusinessSchema, breadcrumbSchema } from "../../seo/schema";
import { hvacPath, isLeesHost, LEES_FAVICON, LEES_CANONICAL_ORIGIN } from "../../seo/hosts";
import { BUSINESS } from "../../data/business";
import { LEES_CONTACT, LEES_SOCIAL } from "../../data/leesContent";
import { HVAC_LOGO, HVAC_NAV } from "../../data/leesServices";
import HVACQuoteForm from "./HVACQuoteForm";
import useScrollOnNavigate from "../../lib/useScrollOnNavigate";

const LOGO_TEXT = "LEE'S HEATING & COOLING";
export const hvacSameAs = () => LEES_SOCIAL.filter((s) => s.url).map((s) => s.url);
export const navHref = (n) => (n.absolute ? n.path : hvacPath(n.path));

const STYLES = `
  .hvac-grain { background-image: linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.85)), url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='4' height='4'><rect width='4' height='4' fill='%23111111'/><circle cx='1' cy='1' r='0.4' fill='%23222'/></svg>"); }
  .hvac-stencil { font-family: 'Oswald','Arial Black',sans-serif; letter-spacing: 0.02em; }
  .hvac-stamp { text-shadow: 1px 1px 0 rgba(0,0,0,0.4); }
  .hvac-divider { height:4px; background: repeating-linear-gradient(45deg,#8B1E1E 0 12px,#111 12px 24px); }
  .hvac-card { background:#1a1a1a; border:1px solid #2a2a2a; transition: border-color .2s ease; }
  .hvac-card:hover { border-color:#8B1E1E; }
`;

// Text wordmark (the previous remote logo image returned 403 and always fell back to this).
export function LogoMark() {
  return (
    <span className="hvac-stencil h-12 flex items-center text-[#E55B3A] font-bold uppercase tracking-wider text-base md:text-lg leading-none">
      <span className="mr-2 h-9 w-9 bg-[#E55B3A] text-black flex items-center justify-center text-lg" aria-hidden="true">L</span>
      {LOGO_TEXT}
    </span>
  );
}

function HVACHeader() {
  const { pathname } = useLocation();
  return (
    <header className="bg-black border-b-2 border-[#8B1E1E] sticky top-0 z-40">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-5 py-3">
        <Link to={hvacPath("/")} className="flex items-center gap-3" data-testid="hvac-logo"><LogoMark /></Link>
        <a href={LEES_CONTACT.phoneHref} className="inline-flex items-center gap-2 bg-[#8B1E1E] hover:bg-[#A6271F] text-white font-bold px-4 py-2 rounded-sm text-sm tracking-wide transition-colors" data-testid="hvac-header-call">
          <Phone size={14} /> {LEES_CONTACT.phone}
        </a>
      </div>
      <nav className="border-t border-white/10" aria-label="HVAC site">
        <div className="max-w-6xl mx-auto px-5 flex gap-5 md:gap-7 overflow-x-auto whitespace-nowrap py-2.5 [scrollbar-width:none]">
          {HVAC_NAV.map((n) => {
            const href = navHref(n);
            const active = pathname === href;
            return (
              <Link key={n.path} to={href} className={`hvac-stencil text-xs md:text-[13px] uppercase tracking-[0.16em] font-bold transition-colors ${active ? "text-[#FF6B4A]" : "text-slate-300 hover:text-white"}`} data-testid={`hvac-nav-${n.path.replace(/\W+/g, "") || "home"}`}>
                {n.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </header>
  );
}

function HVACQuoteSection({ defaultService }) {
  return (
    <section id="hvac-quote" className="bg-white text-[#111111] py-16 md:py-20 scroll-mt-28" data-testid="hvac-quote-section">
      <div className="max-w-3xl mx-auto px-5">
        <div className="text-center mb-10">
          <p className="hvac-stencil text-[#8B1E1E] text-xs font-bold uppercase tracking-[0.3em] mb-2">Request Service</p>
          <h2 className="hvac-stencil text-3xl md:text-4xl font-bold uppercase">Get an honest estimate.</h2>
          <p className="text-slate-600 mt-3 text-sm md:text-base">Tell us what&apos;s going on. Faster response if you mark it as an emergency.</p>
        </div>
        <HVACQuoteForm defaultService={defaultService} />
      </div>
    </section>
  );
}

function HVACFooter() {
  return (
    <footer className="bg-black border-t-2 border-[#8B1E1E]">
      <div className="max-w-6xl mx-auto px-5 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <LogoMark />
          <p className="text-sm text-slate-400 leading-relaxed mt-3">Family-owned HVAC serving Northwest Indiana. Inspired by the legacy of a hardworking Vietnam veteran.</p>
        </div>
        <div>
          <h3 className="hvac-stencil text-white text-sm font-bold uppercase tracking-[0.25em] mb-3">Contact</h3>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><a href={LEES_CONTACT.phoneHref} className="hover:text-white" data-testid="hvac-footer-call">{LEES_CONTACT.phone}</a></li>
            <li><a href={LEES_CONTACT.emailHref} className="hover:text-white break-all">{LEES_CONTACT.email}</a></li>
            <li>Serving NW Indiana</li>
          </ul>
        </div>
        <div>
          <h3 className="hvac-stencil text-white text-sm font-bold uppercase tracking-[0.25em] mb-3">Explore</h3>
          <ul className="grid grid-cols-2 gap-2 text-sm text-slate-300">
            {HVAC_NAV.map((n) => (
              <li key={n.path}><Link to={navHref(n)} className="hover:text-white" data-testid={`hvac-footer-${n.path.replace(/\W+/g, "")}`}>{n.label}</Link></li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-5 py-6 text-center">
          <p className="hvac-stencil text-white font-bold text-sm uppercase tracking-[0.25em]">Lee&apos;s Heating &amp; Cooling</p>
          <p className="hvac-stencil text-[#EF4444] text-xs mt-1 font-bold uppercase tracking-[0.25em]">Serving You Through Every Season</p>
          <p className="text-xs text-slate-400 mt-4">© {new Date().getFullYear()} Lee&apos;s Heating &amp; Cooling. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

// Shared chrome for every Lee's page: SEO head, header/nav, quote form, footer.
export default function HVACShell({ path, title, description, crumb, schemas = [], defaultService, testid, children }) {
  useScrollOnNavigate();
  // HVAC pages always canonicalize to leesheatingnwi.com. Compatibility views under
  // /hvac/* on any other hostname stay usable but are noindex,follow (runtime hostname check,
  // so pages rendered under leesheatingnwi.com are never affected).
  const onLees = isLeesHost();
  const leesUrl = (p) => `${LEES_CANONICAL_ORIGIN}${p === "/" ? "/" : p}`;
  const crumbs = [{ name: "Home", url: leesUrl("/") }];
  if (crumb) crumbs.push({ name: crumb, url: leesUrl(path) });
  return (
    <div className="min-h-screen bg-[#111111] text-white font-sans" data-testid={testid}>
      <HeadTags
        title={title}
        description={description}
        path={hvacPath(path)}
        canonical={leesUrl(path)}
        robots={onLees ? undefined : "noindex,follow"}
        image={HVAC_LOGO}
        favicon={isLeesHost() ? LEES_FAVICON : BUSINESS.logoSrc}
        siteName="Lee's Heating & Cooling"
        schemas={[leesBusinessSchema({ sameAs: hvacSameAs() }), ...schemas, breadcrumbSchema(crumbs)]}
      />
      <style>{STYLES}</style>
      <HVACHeader />
      <main>{children}</main>
      <HVACQuoteSection defaultService={defaultService} />
      <HVACFooter />
    </div>
  );
}
